import React, { useState, useRef, useCallback, useEffect } from "react";
import { List, Skeleton } from "antd";
import NoteItem from "@/components/Notes/components/NoteItem";

const CustomNotesList = ({
  notes: propNotes,
  totalItems,
  getNotes,
  loading,
  page,
  pageSize,
  onNotesCountChange,
  onlyList,
}) => {
  const [notes, setNotes] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const observerRef = useRef();

  // Cleanup observer on component unmount
  useEffect(
    () => () => {
      if (observerRef.current) observerRef.current.disconnect();
    },
    [],
  );

  // Append new notes to the existing list
  useEffect(() => {
    if (page > 1) {
      setNotes(prevNotes => [...prevNotes, ...propNotes]);
    } else {
      setNotes(propNotes);
    }
  }, [propNotes, page]);

  const fetchMoreNotes = useCallback(() => {
    if (loading || isFetching || totalItems <= notes.length) return;
    setIsFetching(true);
    getNotes(page + 1, pageSize).finally(() => {
      setIsFetching(false);
    });
  }, [loading, isFetching, notes.length, totalItems, page, pageSize, getNotes]);

  const lastItemRef = useCallback(
    node => {
      if (loading || isFetching) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchMoreNotes();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [fetchMoreNotes, loading, isFetching],
  );

  if (isFetching || loading) {
    return (
      <div
        style={{
          padding: 10,
          width: "100%",
          ...(onlyList && { minWidth: 440 }),
        }}
      >
        <Skeleton paragraph={{ rows: 2 }} active />
      </div>
    );
  }

  return (
    <div style={{ padding: 10 }}>
      <List
        itemLayout="horizontal"
        dataSource={notes}
        renderItem={(item, index) => (
          <div
            ref={index === notes.length - 1 ? lastItemRef : null} // Attach ref to the last item
          >
            <NoteItem
              key={item.id}
              item={item}
              getNotes={getNotes}
              onNotesCountChange={onNotesCountChange}
              itemIndex={index}
              totalItems={totalItems}
            />
          </div>
        )}
        style={{
          width: "100%",
          ...(onlyList && { minWidth: 440 }),
        }}
      />
    </div>
  );
};

export default CustomNotesList;
